import { useEffect, useState, createContext, useContext } from "react";
import { CircularProgress, Typography, Button, Checkbox, Chip, Tooltip } from "@mui/material";
import { tss } from "tss-react";
import { StorageUtils } from "services/UtilsService";
import { TreeItem, SimpleTreeView } from "@mui/x-tree-view";
import { Scrollbars } from "react-custom-scrollbars";
import { BreadCrumbs, StyledTreeItem, CircularProgressWithLabel } from "components";
import { getCachedData } from "api/Cache";
import { icons } from "constants/viewer";
import { useHistory } from "react-router-dom";
import { getFileIcon } from "actions/GlobalActions";
import EventsEmitter from "events/EventsEmitter";
import DeleteItemsPanel from "panels/DeleteItemsPanel";
import { FoldersActions } from "actions";
import { Public } from "@mui/icons-material";
import { ReactComponent as File } from "assets/images/file.svg";
import commonStyles, {
    folderColor,
    headerHeight,
    viewsHeaderHeight,
    rowHeight,
    panelBackgroundColor,
} from "styles/common";
import viewsStyles from "styles/views";
import { useSelector } from 'react-redux';
import useSwalWrapper from 'themes/jumbo/@jumbo/vendors/sweetalert2/hooks';
import _ from 'utils/@lodash';
import { getAccessLevel } from "actions/ViewerTools";
import { useVersioning } from "constants/common";
import { Div } from 'themes/jumbo/@jumbo/shared';

const useStyles: any = tss.create(() => ({
    header: {
        position: "relative",
        flexDirection: "row",
        paddingLeft: 12,
        paddingTop: 12,
        width: "100%",
        alignItems: "center",
        verticalAlign: "top",
    },
    actionButton: {
        marginTop: 3,
        height: 30,
    },
    tree: {
        paddingTop: 7.75,
        paddingBottom: 7.75,
        height: "100%",
        maxWidth: "100%",
        backgroundColor: panelBackgroundColor,
        boxShadow: "1px 1px 13px 0px #00000012",
        width: "100%",
        flexGrow: 0,
    },
    layerIcon: {
        display: "inline-flex",
        alignItems: "center",
    },
    firstCol: {
        maxWidth: 340,
        textAlign: "left",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        cursor: "pointer",
    },
    wrapper: {
        display: "flex",
        flexWrap: "nowrap",
        width: "100%",
        maxWidth: "100%",
        alignItems: "center",
        height: rowHeight,
    },
    fluid: {
        flex: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: "50px",
        " & p ": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
    },
    fluidContent: {
        flexGrow: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    chipContent: {
        flexGrow: 4,
        maxWidth: 450,
        display: "inline-block",
        whiteSpace: "nowrap",
    },
    chip: {
        marginLeft: 7,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    first: {
        paddingRight: "10px",
        maxWidth: 340,
    },
    noResult: {
        display: "inline-flex",
        alignItems: "center",
        height: 40,
    },
    checkbox: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: rowHeight,
        height: rowHeight,
    },
    chipContainer: {
        width: "100%",
        position: "relative",
        display: "inline-flex",
        top: 9,
        left: 19,
    },
}));

function getIcon(entity: any, classes: any) {
    var fileName = getFileIcon(entity);
    //console.log("fileName:", fileName);
    if (!fileName) {
        return <File className={classes.icon} />;
    }
    const Icon = icons[fileName];
    return (
        <div className={classes.layerIcon}>
            <Icon className={classes.icon} />
        </div>
    );
}

function MainPanel(props: { data: any; setSelectedItem: (id: string) => void }) {
    const { classes: common } = commonStyles();
    const { classes, cx } = useStyles();
    const { classes: viewsClasses } = viewsStyles();
    const { data, setSelectedItem } = props;
    const entities = data["entities"];
    const projects = data["projects"];

    const [filter, setFilter] = useState(StorageUtils.loadFromSessionStorage("filter") || "all");
    const [reload, setReload] = useState(false);
    const [list, setList] = useState<any>(Object.entries(entities));
    const [checkItems, setCheckedItems] = useState<string[]>([]);
    var currentList: any = [];
    const { 
        defaultProjects,
    } = useContext<any>(IssueContext);


    useEffect(() => {
        var updateFilter = (filter: string, isReload?: boolean) => {
            let newList: any = [...Object.entries(entities)];
            if (filter !== "all") {
                newList = newList.filter((entries: any) => {
                    return entries[0] === filter;
                });
            }
            newList = newList.filter((entry: any) => /*!demoProjectsHidden || */!defaultProjects.includes(entry[0])) 

            setList(newList);
            if (!isReload) {
                setReload(true);
            }
            setFilter(filter);
        };

        if (data.loaded) {
            FoldersActions.buildFolderTree(filter, "0", () => {});
        }
        EventsEmitter.on("updateFilter", updateFilter);
        return () => {
            EventsEmitter.removeEventListener("updateFilter", updateFilter);
        };
    }, [reload, entities, filter, data]);

    useEffect(() => {
        var newList: any = [...Object.entries(entities)];
        if (filter !== "all") {
            newList = newList.filter((entries: any) => {
                return entries[0] === filter;
            });
        }
        newList = newList.filter((entry: any) => /*!demoProjectsHidden || */!defaultProjects.includes(entry[0])) 

        setList(newList);
    }, [data, entities, filter]);

    const handleCheckboxChange = (entity: any, checked: boolean) => {
        const itemsRef = checkItems;
        checked ? itemsRef.push(entity.ID) : itemsRef.splice(itemsRef.indexOf(entity.ID), 1);
        setCheckedItems(itemsRef);
        EventsEmitter.emit("updateCatalogList", checkItems);
    };

    return (
        <div className={viewsClasses.main}>
            <Scrollbars
                autoHeight
                autoHeightMax={"calc(100vh - " + (viewsHeaderHeight + headerHeight + 100) + "px)"}
                autoHide
                renderTrackHorizontal={(props: any) => (
                    <div {...props} style={{ display: "none" }} className="track-horizontal" />
                )}
            >
                <SimpleTreeView className={classes.tree} defaultExpandedItems={["1"]} multiSelect>
                    {list.map((entry: any, index: number) => {
                        var id = entry[0];
                        var pending: any = Object.values(entry[1]).filter((p: any) => {
                            return p.state === "ERROR" && p.name;
                        });
                        const isDefaultProject = defaultProjects.includes(id);
                        const projectAccessLevel = getAccessLevel(id);
                        const hasFullAccess = projectAccessLevel === 'full';

                        if (projects[id] && pending.length > 0) {
                            currentList.push(projects[id]);
                            return (
                                <StyledTreeItem
                                    nodeId={"project-" + index}
                                    label={
                                        <div className={classes.wrapper}>
                                            <Public
                                                className={common.icon}
                                                style={{
                                                    color: folderColor,
                                                }}
                                            />
                                            <div className={classes.fluid}>
                                                <div className={classes.fluidContent}>
                                                    <Typography
                                                        variant="body2"
                                                        className={cx(common.mediumLabel, classes.fluidContent)}
                                                    >
                                                        {projects[id].name}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    key={"project-" + index}
                                >
                                    {pending.map((p: any, index1: number) => {
                                        var strPath = FoldersActions.getPathFromEntity(p);
                                        var workflow: any = FoldersActions.getWorkflowFromEntity(p);
                                        let strPathShort = strPath;
                                        if (strPath && strPath.length > 50) {
                                            strPathShort = strPath.substring(0, 45) + "...";
                                        }
                                        //let products = workflow && workflow["products"].join(" - ");
                                        let products = workflow?.products?.join(" - ") || "";
                                        let productsShort = products;
                                        if (products && products.length > 50) {
                                            productsShort = products.substring(0, 45) + "...";
                                        }
                                        let errorInfo = p.error_info ? atob(p.error_info) : "Unknown Error";
                                        let errorInfoShort = errorInfo;
                                        if (errorInfo && errorInfo.length > 150) {
                                            errorInfoShort = errorInfo.substring(0, 145) + "...";
                                        }
                                        return (
                                            <StyledTreeItem
                                                nodeId={"pending-" + id + "-" + index1}
                                                onClick={() => setSelectedItem(p.ID)}
                                                label={
                                                    <div className={classes.wrapper}>
                                                        <div className={classes.checkbox}>
                                                            <Checkbox
                                                                edge="start"
                                                                tabIndex={-1}
                                                                disableRipple
                                                                disabled={isDefaultProject || !hasFullAccess}
                                                                checked={checkItems.includes(p.ID) && hasFullAccess && !isDefaultProject}
                                                                onChange={(_e, checked) => {
                                                                    if (isDefaultProject || !hasFullAccess) return;
                                                                    handleCheckboxChange(p, checked)
                                                                }}
                                                                inputProps={{
                                                                    "aria-labelledby": `checkbox-list-label-${index}`,
                                                                }}
                                                            />
                                                        </div>
                                                        {getIcon(p, common)}
                                                        <div className={cx(classes.fluid, classes.first)}>
                                                            <div className={classes.fluidContent}>
                                                                <Tooltip title={p.name}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        className={common.mediumLabel}
                                                                    >
                                                                        {p.name}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                        { useVersioning && (
                                                            <div className={cx(classes.fluid)}>
                                                                <div className={classes.chipContent}>
                                                                    {workflow === null ? (
                                                                        <div>
                                                                            <Chip
                                                                                label="Not Versioned"
                                                                                size="small"
                                                                                className={classes.chip}
                                                                            />
                                                                            <Tooltip title={strPath}>
                                                                                <Chip
                                                                                    label={strPathShort}
                                                                                    size="small"
                                                                                    className={classes.chip}
                                                                                />
                                                                            </Tooltip>
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            <Chip
                                                                                label={"Version: " + workflow["version"]}
                                                                                size="small"
                                                                                className={classes.chip}
                                                                                style={{
                                                                                    backgroundColor: folderColor,
                                                                                }}
                                                                            />
                                                                            <Tooltip title={products}>
                                                                                <Chip
                                                                                    label={productsShort}
                                                                                    size="small"
                                                                                    className={classes.chip}
                                                                                />
                                                                            </Tooltip>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className={cx(classes.fluid)}>
                                                            <div className={classes.fluidContent}>
                                                                <Tooltip title={errorInfo}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        className={common.mediumLabel}
                                                                    >
                                                                        {errorInfoShort}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                                key={"pending-" + index1}
                                            />
                                        );
                                    })}
                                </StyledTreeItem>
                            );
                        }
                        return <div key={"project-" + index} />;
                    })}
                    {currentList.length === 0 ? (
                        <TreeItem className={cx(common.mediumLabel, classes.noResult)} itemId="2" label="No Issues" />
                    ) : (
                        ""
                    )}
                </SimpleTreeView>
            </Scrollbars>
        </div>
    );
}

function SidePanel(props: { setOpen: any; selectedItem: string }) {
    const { classes } = useStyles();
    const { classes: viewsClasses } = viewsStyles();
    const history = useHistory();
    const { setOpen, selectedItem } = props;
    const { 
        //hasFullAccess,
        sweetAlerts
    } = useContext<any>(IssueContext);

    //console.log("hasFullAccess : " + hasFullAccess)

    const goToFolder = (hash: string | void) => {
        EventsEmitter.emit("changeTab", "catalog");
        history.push("/catalog/" + selectedItem + (hash ? "#" + hash : ""));
        history.goForward();
    };

    const deleteItems = (evt: any) => {
        /*if (!hasFullAccess) {
            sweetAlerts();
            return;
        }*/
        setOpen("deleteItems");
    };

    return (
        <div className={viewsClasses.side}>
            <div className={classes.header}>
                <div>
                    <Button
                        color="primary"
                        className={classes.actionButton}
                        onClick={() => {
                            goToFolder("processlog");
                        }}
                        disabled={selectedItem === ""}
                    >
                        Show Error Log
                    </Button>
                </div>
                { false && (
                    <div>
                        <Button
                            color="primary"
                            className={classes.actionButton}
                            onClick={deleteItems}
                            disabled={FoldersActions.selectedItems.length === 0}
                        >
                            Delete Issue
                        </Button>
                    </div>
                )}
                <div>
                    <Button
                        color="primary"
                        className={classes.actionButton}
                        onClick={() => {
                            goToFolder();
                        }}
                        disabled={selectedItem === ""}
                    >
                        Show in Catalog
                    </Button>
                </div>
            </div>
        </div>
    );
}

export const IssueContext = createContext({});

export default function Issues() {
    const { classes: common, cx } = commonStyles();
    const { classes: viewsClasses } = viewsStyles();
    const data: any = getCachedData();
    const [loadData, setLoadData] = useState(false);
    const [selectedItem, setSelectedItem] = useState("");
    const [open, setOpen] = useState("");
    const [update, setUpdate] = useState(false);
    const [progress, setProgress] = useState(0);
	const storeAuthData = useSelector((state: any) => state.auth);
    const [hasFullAccess, setHasFullAccess] = useState(false);
    const [onlyDefaultProjects, setOnlyDefaultProjects] = useState(false);
    const { hasActiveSubscription = false, projectAccessLevel:access_level = "read", defaultProjects } = storeAuthData;
    const [projects, setProjects] = useState<any>(null);
    const history = useHistory();
    const demoProjectsHidden = Number(localStorage.getItem('hideDemoProjects') || 0) > 0;
    const Swal = useSwalWrapper();

    const sweetAlerts = () => {
        Swal.fire({
        title: 'Your account has limited access!',
        text: 'We have limited your access to some features. Please upgrade your current plan to access this feature.',
        imageWidth: 450,
        imageHeight: 200,
        confirmButtonText: "Upgrade my plan",
        showCloseButton: true,
        }).then((result) => {
            result.isConfirmed && history.push("/profile");
        });
    };

    useEffect(() => {
        setHasFullAccess(hasActiveSubscription/* && access_level === 'full'*/)
    }, [access_level, hasActiveSubscription])

    useEffect(() => {
        const _projects = JSON.parse(JSON.stringify(data["projects"]));
        if (_.isEqual(projects, _projects)) return;

        setProjects(_projects)
    }, [data.projects])

    useEffect(() => {
        if (!projects) return;
        const userProjects = Object.keys(projects).filter((item: string) => !defaultProjects.includes(item))
        setOnlyDefaultProjects(userProjects.length < 1);
    }, [projects, defaultProjects])

    useEffect(() => {
        const handleListBoxSelectedChange = (selectedItems: string[]) => {
            FoldersActions.selectedItems = selectedItems;
            setUpdate((prevUpdate) => !prevUpdate);
        };

        const handleProgress = (curProgress: number) => {
            setProgress(curProgress);
        };

        if (data.loaded) {
            setLoadData(true);
        }
        EventsEmitter.on("projects_progress", handleProgress);
        EventsEmitter.on("updateCatalogList", handleListBoxSelectedChange);
        return () => {
            EventsEmitter.removeEventListener("updateCatalogList", handleListBoxSelectedChange);
            EventsEmitter.removeEventListener("projects_progress", handleProgress);
        };
    }, [data]);

    useEffect(() => {
        var reload = () => {
            setUpdate((prevUpate) => !prevUpate);
        };

        EventsEmitter.on("signalr_update", reload);
        EventsEmitter.on("dataUpdate", reload);
        return () => {
            EventsEmitter.removeEventListener("signalr_update", reload);
            EventsEmitter.removeEventListener("dataUpdate", reload);
        };
    }, [update]);

    useEffect(() => {
        return () => {
            FoldersActions.selectedItems = [];
        };
    }, []);

    if (!data.loaded || !loadData) {
        return progress === 0 ? <CircularProgress /> : <CircularProgressWithLabel value={progress} />;
    }

    const handleClose = () => {
        setOpen("");
    };
    const IssueContextValue = {
        hasFullAccess,
        defaultProjects,
        onlyDefaultProjects,
        sweetAlerts,
        demoProjectsHidden
    }

    return (
        <IssueContext.Provider value={IssueContextValue}>
            <div className={viewsClasses.main} style={{ width: "100%", height: "100%" }}>
                <div className={cx(common.middle, viewsClasses.topHeader)}>
                    <BreadCrumbs label="Issues" className='mainTitle' />
                </div>
                <Div className={viewsClasses.content} sx={{flexDirection: {xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row'}}}>
                    <MainPanel data={data} setSelectedItem={setSelectedItem} />
                    <SidePanel setOpen={setOpen} selectedItem={selectedItem} />
                </Div>
                <DeleteItemsPanel open={open === "deleteItems"} handleClose={handleClose} />
            </div>
        </IssueContext.Provider>
    );
}
